/**
 * material_mapping.ts — 자재별 라인 매핑(material_mappings) 도메인 타입.
 *
 * "이 자재가 어느 라인(들)에 들어가는가" 의 시점별 이력. SCD Type-2 — INSERT only.
 * 자재가 최상위, 라인이 자식 (모래 → s1+s2 같은 1자재 N라인 가능).
 *
 * Firestore: "material_mappings" — 자동 ID. 1행 = 1자재의 1시점 매핑.
 *
 * 라인 코드 (배합비 13컴포넌트 - bs - w/w2 = 11):
 *   골재    g1, g2 (굵은골재 25mm/19mm)
 *   잔골재  s1, s2, s3 (모래 1/2/부순모래)
 *   결합재  c1, c2, c3 (시멘트 1종/2종/플라이애쉬)
 *   혼화제  ad1, ad2, ad3 (일반1/고강/일반2)
 */

import type { MaterialCategory } from "@/domain/material";

export const LINE_CODES = [
  "g1", "g2",
  "s1", "s2", "s3",
  "c1", "c2", "c3",
  "ad1", "ad2", "ad3",
] as const;
export type LineCode = (typeof LINE_CODES)[number];

export type LineGroup = "굵은골재" | "잔골재" | "결합재" | "혼화제";

export const LINE_GROUPS: Readonly<Record<LineCode, LineGroup>> = {
  g1: "굵은골재", g2: "굵은골재",
  s1: "잔골재", s2: "잔골재", s3: "잔골재",
  c1: "결합재", c2: "결합재", c3: "결합재",
  ad1: "혼화제", ad2: "혼화제", ad3: "혼화제",
};

/**
 * 라인의 **설비 이름** — "어느 사일로/탱크/야적인가" 자체를 가리킨다. (예: "C1 사일로")
 *
 * ⚠️ 자재명이 아니다. 라인을 고르는 UI(투입 라인·출고 라인)는 반드시 이걸 쓴다.
 * 옛 `LINE_DEFAULT_LABEL`(c1="시멘트(1종)" 같은 하드코딩)은 매핑이 바뀐 라인에서 틀린
 * 이름을 보여줘 폐기했다 — c1 에 2종을 넣기 시작하면 "시멘트(1종)"은 거짓이 된다.
 * 라인에 **실제로 담긴 자재명**이 필요하면 line_capacity.ts 의 `lineLabel()` 을 쓴다.
 */
export function lineSlotLabel(line: LineCode): string {
  const kind =
    LINE_GROUPS[line] === "혼화제" ? "탱크" : LINE_GROUPS[line] === "결합재" ? "사일로" : "야적";
  return `${line.toUpperCase()} ${kind}`;
}

export interface MaterialMapping {
  /** Firestore 자동 ID */
  id: string;
  /** material_categories.code (예: "SC1", "SD") */
  mat_code: string;
  /** 이 자재가 들어가는 라인들. 빈 배열 = 미연결 */
  line_codes: LineCode[];
  /** 주 거래처 (자유 입력) */
  supplier: string | null;
  /** 매핑 시작일 YYYY-MM-DD (자동: 저장 시 today) */
  effective_date: string;
  /** 활성화 여부. false면 매핑 비활성 (사용 안 함). default true */
  is_active: boolean;
  /** 변경 사유, 비고 */
  memo: string;
  /** 누가 변경했는지 — Firebase Auth UID 또는 표시 이름 */
  created_by: string;
  created_at?: Date;
}

/** 신규 매핑 입력 — id/created_at 없는 MaterialMapping */
export type MaterialMappingDraft = Omit<MaterialMapping, "id" | "created_at">;

/**
 * 모든 매핑 행 → 자재별 시점 desc 이력.
 * 자재 1개 = [최신, 이전, ...] 배열.
 */
export function deriveMatHistory(rows: MaterialMapping[]): Map<string, MaterialMapping[]> {
  const out = new Map<string, MaterialMapping[]>();
  for (const r of rows) {
    if (!out.has(r.mat_code)) out.set(r.mat_code, []);
    out.get(r.mat_code)!.push(r);
  }
  for (const [k, list] of out) {
    list.sort((a, b) => {
      if (a.effective_date !== b.effective_date) {
        return a.effective_date < b.effective_date ? 1 : -1;
      }
      const ta = a.created_at instanceof Date ? a.created_at.getTime() : 0;
      const tb = b.created_at instanceof Date ? b.created_at.getTime() : 0;
      return tb - ta;
    });
    out.set(k, list);
  }
  return out;
}

/** 오늘(현지 날짜) YYYY-MM-DD. toISOString 은 UTC 라 한국 0~9시엔 어제가 된다(전환일 아침에 옛 배정이 보이는 문제). */
export function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** YYYY-MM 의 말일 YYYY-MM-DD */
export function ymLastDay(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
}

/**
 * 월 화면이 사일로 배정을 볼 기준일 — 지난 달은 말일, 이번 달은 오늘.
 * (아직 오지 않은 전환 예약은 이번 달 화면에 미리 반영하지 않는다)
 */
export function mappingAsOfForYm(ym: string, today: string = localToday()): string {
  const end = ymLastDay(ym);
  return end < today ? end : today;
}

/**
 * 특정 시점에 active한 자재별 매핑 1건씩 반환.
 * 사일로 배정은 날짜로 바뀐다(계절 전환) — 기록을 다룰 때는 그 기록의 날짜를 asOf 로 넘긴다.
 * @param asOf YYYY-MM-DD. 미지정 시 오늘(현지 날짜).
 */
export function getActiveByMat(
  rows: MaterialMapping[],
  asOf: string = localToday(),
): Map<string, MaterialMapping | null> {
  const history = deriveMatHistory(rows);
  const out = new Map<string, MaterialMapping | null>();
  for (const [code, list] of history) {
    const active = list.find((r) => r.effective_date <= asOf) ?? null;
    out.set(code, active);
  }
  return out;
}

/**
 * 라인 1개의 active 자재 lookup (역방향) — 배합비/거래처/단가에서 line_code → mat_code 찾을 때.
 * 한 라인에 매핑된 자재가 여러 개일 경우(설정 오류) 첫 매치 반환.
 */
export function getMatForLine(
  rows: MaterialMapping[],
  line: LineCode,
  asOf?: string,
): MaterialMapping | null {
  const active = getActiveByMat(rows, asOf);
  for (const m of active.values()) {
    if (m && m.line_codes.includes(line)) return m;
  }
  return null;
}

/**
 * 출고 라인 게이팅용 active 매핑 — getActiveByMat 결과에 자재 활성(is_active) cascade 적용.
 *
 * material_categories 에서 is_active === false 인 자재는 매핑이 살아있어도 출고 라인에서
 * 제외해야 한다(단일 기준 = material.is_active). 이 헬퍼가 그 자재 엔트리를 null 로 강등.
 *
 * 활성 판정은 `is_active !== false` (undefined/true → 활성). Material/lib.ts isMatActive 와 동일 규칙.
 *
 * @param cats material_categories — code + is_active 만 필요.
 * @param asOf YYYY-MM-DD. getActiveByMat 에 위임.
 */
export function getOutboundActiveByMat(
  rows: MaterialMapping[],
  cats: Pick<MaterialCategory, "code" | "is_active">[],
  asOf?: string,
): Map<string, MaterialMapping | null> {
  const base = getActiveByMat(rows, asOf);
  const inactive = new Set(
    cats.filter((c) => c.is_active === false).map((c) => c.code),
  );
  const out = new Map<string, MaterialMapping | null>(base);
  for (const code of inactive) {
    if (out.has(code)) out.set(code, null);
  }
  return out;
}

/** 라인 1개의 출고 자재 판정 결과 */
export interface LineMatPick {
  /** 출고 기록에 쓸 자재 코드 */
  mat_code: string;
  /** 같은 통합 관리 그룹으로 묶였으면 그 그룹 이름 (예: "혼화제(일반)") — 화면은 이 이름으로 보여 준다 */
  unifyLabel?: string;
  /** 이 라인을 잡은 자재들 (코드순) */
  mats: string[];
}

/**
 * 라인 → 출고 자재. 한 라인을 여러 자재가 잡고 있을 때의 규칙:
 *   - 모두 같은 통합 관리 그룹(unify_enabled + 같은 분류 + 같은 unify_group)이면 **그룹으로 묶는다** —
 *     출고는 그룹 단위(예: AD1·AD3 → 혼화제(일반), 1+3 탱크 구조)이고 기록 자재는 그룹의 첫 코드로 고정.
 *   - 아니면 설정 겹침 — 코드순 첫 자재(항상 같은 결과가 나오게).
 * 예전엔 Map 순회 순서상 "먼저 나온 자재"가 이겨 같은 라인이 어느 자재로 기록될지 정해지지 않았다.
 */
export function pickLineMats(
  active: Map<string, MaterialMapping | null>,
  cats: readonly Pick<MaterialCategory, "code" | "category" | "unify_group" | "unify_enabled">[],
): Map<LineCode, LineMatPick> {
  const byLine = new Map<LineCode, string[]>();
  for (const [code, m] of active) {
    if (!m || m.is_active === false) continue;
    for (const l of m.line_codes) {
      if (!byLine.has(l)) byLine.set(l, []);
      byLine.get(l)!.push(code);
    }
  }
  const out = new Map<LineCode, LineMatPick>();
  for (const [line, list] of byLine) {
    const mats = [...new Set(list)].sort();
    let unifyLabel: string | undefined;
    if (mats.length > 1) {
      const cs = mats.map((c) => cats.find((x) => x.code === c));
      const g = cs[0]?.unify_group?.trim();
      const same = !!g && cs.every((c) => c && c.unify_enabled === true && c.unify_group?.trim() === g && c.category === cs[0]!.category);
      if (same) unifyLabel = g;
    }
    out.set(line, { mat_code: mats[0], unifyLabel, mats });
  }
  return out;
}

